import React from 'react';
import { Stack } from '@mui/material';

import { getHiddenEntitySnapTargetTypesForVisibleObjects } from '../../../../store/selectors/lineSnappingSelectors';
import {
  getConfiguredPointSnappingContextAndSettings,
  getDisabledActiveSnaps,
} from '../../../../store/selectors/snappingSettingsSelectors';
import { getTrussEnvelopeInsertionInformation } from '../../../../store/selectors/trussEnvelopeInsertionInformationSelectors';
import { layoutSlice } from '../../../../store/slices/layoutSlice';
import { useActualTrussEnvelopeSnappingSettings } from '../../../../three/layout/truss-envelopes/TrussEnvelopeInsertionVisual';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { LockedTrussEnvelopeTargetControl } from '../../../interface/subpanels/insertions/truss-envelope/LockedTrussEnvelopeTargetControl';
import { FloatingBoxTopRightButton } from '../common/FloatingBoxTopRightButton';
import { StandardFloatingBox } from '../common/StandardFloatingBox';
import { PointSnappingSettingsControls } from './controls/PointSnappingSettingsControls';
import { SnappingSettingsResetButton } from './controls/SnappingSettingsResetButton';
import { SnappingTargetControls } from './controls/SnappingTargetControls';
import { TrussEnvelopeSnappingConfiguration } from './controls/TrussEnvelopeSnappingConfiguration';
import { areSnappingSettingsRelevant } from './selectors/areSnappingSettingsRelevant';

export function SnappingSettingsFloatingBox() {
  const dispatch = useAppDispatch();
  const contextAndSettings = useAppSelector(getConfiguredPointSnappingContextAndSettings);
  const hiddenTypes = useAppSelector(getHiddenEntitySnapTargetTypesForVisibleObjects);

  const trussEnvelopeInsertion = useAppSelector(getTrussEnvelopeInsertionInformation);

  const enabledForPointSnapping = useAppSelector(areSnappingSettingsRelevant);
  const disabledSnaps = useAppSelector(getDisabledActiveSnaps);
  const enabledForEnvelopeSnapping =
    trussEnvelopeInsertion?.activeMode === 'parallel-line' && !trussEnvelopeInsertion.isInsertionEnvelopeFullyDefined();

  const actualTrussEnvelopeSnappingSettings = useActualTrussEnvelopeSnappingSettings(
    (state) => state.settingsByEnvelope[trussEnvelopeInsertion?.data.guid ?? ''] ?? null,
  );

  if (!enabledForPointSnapping && !enabledForEnvelopeSnapping) return null;
  if (contextAndSettings == null) return null;
  const [context, settings] = contextAndSettings;

  const trussEnvelopeSnappingSettings = trussEnvelopeInsertion?.settings.snapping;
  const isTargetLockedForTrussEnvelopeSnapping = Boolean(trussEnvelopeInsertion?.settings.snapping.lockedTarget);

  return (
    <StandardFloatingBox className="clickable-during-picking">
      <FloatingBoxTopRightButton>
        <SnappingSettingsResetButton
          context={context}
          settings={settings}
          trussEnvelopeSnappingSettings={trussEnvelopeSnappingSettings}
        />
      </FloatingBoxTopRightButton>

      <Stack direction="column" spacing={1}>
        {enabledForPointSnapping && (
          <PointSnappingSettingsControls context={context} settings={settings} disabledSnaps={disabledSnaps} />
        )}

        <SnappingTargetControls
          settings={settings}
          onTargetToggled={(target) => dispatch(layoutSlice.actions.pointSnappingTargetToggled({ context, target }))}
          disabled={isTargetLockedForTrussEnvelopeSnapping}
          disabledTypes={hiddenTypes}
        />

        {enabledForEnvelopeSnapping && (
          <>
            {isTargetLockedForTrussEnvelopeSnapping && (
              <LockedTrussEnvelopeTargetControl condensed sx={{ padding: 0.5 }} />
            )}

            <TrussEnvelopeSnappingConfiguration
              configuredSettings={trussEnvelopeSnappingSettings!}
              actualSettings={actualTrussEnvelopeSnappingSettings}
            />
          </>
        )}
      </Stack>
    </StandardFloatingBox>
  );
}
